import { DateTime } from 'luxon';
import { getRoutes, getRoute } from './routes';
import { search } from './booking';
import { SearchParams, Route } from '../shared/interface';

export interface UpcomingBookingDto {
  id: string;
  date: string;
  routeName: string;
  departureHarbour: string;
  arrivalHarbour: string;
  departureTime: string;
  arrivalTime: string;
  capacity: number;
  bookedSeats: number;
  routeId: string;
  isReturnTrip: boolean;
}

export interface UpcomingBookingsParams {
  fromDate?: string;
  toDate?: string;
  routeId?: string;
}

/**
 * Fetches upcoming bookings for the current tenant using existing search endpoints
 */
export const getUpcomingBookings = async (params?: UpcomingBookingsParams): Promise<UpcomingBookingDto[]> => {
  try {
    let targetRoutes: Route[];
    
    if (params?.routeId) {
      // If a specific route is requested, use getRoute() to get detailed data
      try {
        const specificRoute = await getRoute(params.routeId);
        targetRoutes = [specificRoute];
      } catch (error) {
        console.error('Failed to fetch specific route:', error);
        return [];
      }
    } else {
      // Get all routes for the tenant
      const routes = await getRoutes();
      targetRoutes = routes;
    }

    if (targetRoutes.length === 0) {
      return [];
    }

    const fromDate = params?.fromDate ? DateTime.fromISO(params.fromDate) : DateTime.now();
    const toDate = params?.toDate ? DateTime.fromISO(params.toDate) : DateTime.now().plus({ days: 7 });
    
    const upcomingBookings: UpcomingBookingDto[] = [];

    // For each route, search for bookings across the date range
    for (const route of targetRoutes) {
      const routeBookings = await getBookingsForRoute(route, fromDate, toDate);
      upcomingBookings.push(...routeBookings);
    }

    return upcomingBookings.sort((a, b) => {
      // Sort by date first, then by departure time
      const dateCompare = a.date.localeCompare(b.date);
      if (dateCompare !== 0) return dateCompare;
      return a.departureTime.localeCompare(b.departureTime);
    });

  } catch (error) {
    console.error('Failed to fetch upcoming bookings:', error);
    throw error;
  }
};

/**
 * Gets bookings for a specific route across a date range
 */
const getBookingsForRoute = async (route: Route, fromDate: DateTime, toDate: DateTime): Promise<UpcomingBookingDto[]> => {
  if (!route.partialRoutes || route.partialRoutes.length < 2) {
    return [];
  }

  const bookings: UpcomingBookingDto[] = [];
  
  // Get first and last harbour for the route
  const firstHarbour = route.partialRoutes[0];
  const lastHarbour = route.partialRoutes[route.partialRoutes.length - 1];

  // Search for bookings for each day in the range
  let currentDate = fromDate;
  
  while (currentDate <= toDate) {
    try {
      // Check if this date is valid according to calendar items
      if (isDateValidForRoute(currentDate, route)) {
        const searchParams: SearchParams = {
          routeId: route.id,
          fromHarbourId: firstHarbour.harbourId,
          toHarbourId: lastHarbour.harbourId,
          returnTrip: true,
          departureDate: currentDate.toFormat('yyyy-MM-dd'),
          returnDate: currentDate.toFormat('yyyy-MM-dd'),
          numberOfAdults: 1,
          numberOfChildren: 0
        };

        const searchResult = await search(searchParams);
        
        if (searchResult) {
          // Process departure schedules
          for (const departure of searchResult.departureSchedules) {
            bookings.push({
              id: `${departure.departureSchedule.id}-${currentDate.toFormat('yyyy-MM-dd')}-departure`,
              date: currentDate.toFormat('yyyy-MM-dd'),
              routeName: searchResult.routeName,
              departureHarbour: departure.departureHarbourName,
              arrivalHarbour: departure.arrivalHarbourName,
              departureTime: departure.departureSchedule.departure?.toFormat('HH:mm') || '',
              arrivalTime: departure.arrivalSchedule.arrival?.toFormat('HH:mm') || '',
              capacity: route.capacity,
              bookedSeats: departure.bookedCapacity,
              routeId: route.id,
              isReturnTrip: false
            });
          }

          // Process return schedules
          for (const returnTrip of searchResult.returnSchedules) {
            bookings.push({
              id: `${returnTrip.departureSchedule.id}-${currentDate.toFormat('yyyy-MM-dd')}-return`,
              date: currentDate.toFormat('yyyy-MM-dd'),
              routeName: searchResult.routeName,
              departureHarbour: returnTrip.departureHarbourName,
              arrivalHarbour: returnTrip.arrivalHarbourName,
              departureTime: returnTrip.departureSchedule.departure?.toFormat('HH:mm') || '',
              arrivalTime: returnTrip.arrivalSchedule.arrival?.toFormat('HH:mm') || '',
              capacity: route.capacity,
              bookedSeats: returnTrip.bookedCapacity,
              routeId: route.id,
              isReturnTrip: true
            });
          }
        }
      }
    } catch (error) {
      // Skip this date if search fails
      console.warn(`Failed to search bookings for ${route.name} on ${currentDate.toFormat('yyyy-MM-dd')}:`, error);
    }

    currentDate = currentDate.plus({ days: 1 });
  }

  return bookings;
};

/**
 * Check if a date is valid for a route based on its calendar items
 */
const isDateValidForRoute = (date: DateTime, route: Route): boolean => {
  if (!route.calendarItems || route.calendarItems.length === 0) {
    return true; // If no calendar restrictions, assume it's valid
  }

  const weekday = date.weekday === 7 ? 0 : date.weekday; // Convert Luxon weekday (1-7) to DayOfWeek (0-6)

  return route.calendarItems.some((calendarItem: any) => {
    const start = DateTime.fromISO(calendarItem.start);
    const end = calendarItem.end ? DateTime.fromISO(calendarItem.end) : null;
    
    // Check if date is within the calendar item's date range
    const isInDateRange = date >= start && (!end || date <= end);
    
    // Check if the weekday is allowed
    const isWeekdayAllowed = calendarItem.weekDays.includes(weekday);
    
    return isInDateRange && isWeekdayAllowed;
  });
};
